import type { CartLine } from "@/graphql/storefront/generated";

/**
 * Meta's browser ID and click ID cookies. The pixel writes them when it loads;
 * the site writes them itself too, so a visitor whose browser blocks the pixel
 * still carries both to the Conversions API.
 */
export const META_BROWSER_ID_COOKIE = "_fbp";
export const META_CLICK_ID_COOKIE = "_fbc";

/** The pixel's own lifetime for both cookies. */
export const META_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

// `fb.{subdomain index}.{creation ms}.{value}`; index 1 is maticrobots.com.
const metaCookieValue = (createdAtMs: number, value: string) =>
  `fb.1.${createdAtMs}.${value}`;

export const metaClickId = (fbclid: string, createdAtMs: number) =>
  metaCookieValue(createdAtMs, fbclid);

export const metaBrowserId = (random: number, createdAtMs: number) =>
  metaCookieValue(createdAtMs, String(random));

/** The fbclid a `_fbc` value was made from. */
export const fbclidOf = (clickId: string): string | undefined =>
  clickId.split(".").slice(3).join(".") || undefined;

const META_COOKIE_PATTERN = /^fb\.\d\.\d{10,13}\.\S{1,500}$/;

export const isMetaCookieValue = (value: string | undefined): value is string =>
  value !== undefined && META_COOKIE_PATTERN.test(value);

export type MetaAddToCartData = {
  content_ids: string[];
  content_type: "product_group";
  value: number;
  currency: string;
};

/** What the browser posts to /api/meta/add-to-cart. */
export type MetaAddToCartRequest = {
  eventId: string;
  eventSourceUrl: string;
  customData: MetaAddToCartData;
};

export const metaAddToCartData = (
  line: Pick<CartLine, "merchandise">,
  quantity: number,
): MetaAddToCartData => ({
  content_ids: [line.merchandise.product.id.split("/").pop() ?? ""],
  content_type: "product_group",
  value: Number(line.merchandise.price.amount) * quantity,
  currency: line.merchandise.price.currencyCode,
});

export type MetaVisitor = {
  ipAddress: string | undefined;
  userAgent: string | undefined;
  browserId: string;
  clickId: string | undefined;
};

/** The Conversions API event exactly as sent, exported for tests. */
export const toMetaAddToCartEvent = (
  request: MetaAddToCartRequest,
  visitor: MetaVisitor,
  eventTimeMs: number,
) => ({
  event_name: "AddToCart",
  event_time: Math.floor(eventTimeMs / 1000),
  event_id: request.eventId,
  event_source_url: request.eventSourceUrl,
  action_source: "website",
  user_data: {
    client_ip_address: visitor.ipAddress,
    client_user_agent: visitor.userAgent,
    fbp: visitor.browserId,
    fbc: visitor.clickId,
  },
  custom_data: request.customData,
});
